import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { api, apiAdmin, formatTurkishDate } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import { useInvitationViewer } from "@/contexts/InvitationViewerContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import AdminLogin from "@/components/AdminLogin";
import EventsTable from "@/components/EventsTable";
import UpcomingListCard from "@/components/UpcomingListCard";
import AutoFitPreview from "@/components/AutoFitPreview";
import SocialMediaCard from "@/components/SocialMediaCard";
import AdminSettings from "@/components/AdminSettings";
import AdManagement from "@/components/AdManagement";
import SocialAndStoriesAdmin from "@/components/SocialAndStoriesAdmin";
import EventForm from "@/components/EventForm";
import EditEventDialog from "@/components/EditEventDialog";
import ShareDialog, { buildEventShareText } from "@/components/ShareDialog";
import {
  Check,
  X,
  FileText,
  Share2,
  LogOut,
  ShieldCheck,
  Trash2,
  RefreshCw,
  Plus,
  Pencil,
  CalendarRange,
  Settings as SettingsIcon,
  Image as ImageIcon,
  Megaphone,
} from "lucide-react";

const AdminPanel = () => {
  const { categoryMap } = useSettings();
  const { openInvitation } = useInvitationViewer();
  const [authed, setAuthed] = useState(() =>
    sessionStorage.getItem("sahder_admin") === "1" &&
    !!sessionStorage.getItem("sahder_admin_pw")
  );
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [shareEvent, setShareEvent] = useState(null);
  const [shareCalendar, setShareCalendar] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [editEvent, setEditEvent] = useState(null);

  const calendarShareRef = useRef(null);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/events");
      setEvents(data);
    } catch (err) {
      toast.error("Etkinlikler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (authed) fetchEvents(); }, [authed]);

  if (!authed) return <AdminLogin onSuccess={() => setAuthed(true)} />;

  const pending = events.filter((e) => e.status === "pending");
  const approved = events.filter((e) => e.status === "approved");
  const rejected = events.filter((e) => e.status === "rejected");

  const handleApprove = async (ev) => {
    try {
      await apiAdmin.patch(`/events/${ev.id}/approve`);
      toast.success("Etkinlik onaylandı");
      fetchEvents();
    } catch { toast.error("Onaylama başarısız"); }
  };
  const handleReject = async (ev) => {
    try {
      await apiAdmin.patch(`/events/${ev.id}/reject`);
      toast.success("Etkinlik reddedildi");
      fetchEvents();
    } catch { toast.error("Reddetme başarısız"); }
  };
  const handleDelete = async (ev) => {
    if (!window.confirm("Bu etkinliği kalıcı olarak silmek istiyor musunuz?")) return;
    try {
      await apiAdmin.delete(`/events/${ev.id}`);
      toast.success("Etkinlik silindi");
      fetchEvents();
    } catch { toast.error("Silme başarısız"); }
  };
  const handleLogout = () => {
    sessionStorage.removeItem("sahder_admin");
    sessionStorage.removeItem("sahder_admin_pw");
    setAuthed(false);
  };

  const handleGeneratePDF = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const now = new Date();
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.setTextColor(138, 37, 44);
    doc.text("SAHDER Etkinlik Raporu", 40, 50);
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(75, 85, 99);
    doc.text(`Rapor tarihi: ${now.toLocaleDateString("tr-TR")} ${now.toLocaleTimeString("tr-TR")}`, 40, 68);
    doc.text(`Toplam: ${events.length}  |  Onayli: ${approved.length}  |  Bekleyen: ${pending.length}  |  Reddedilen: ${rejected.length}`, 40, 82);
    const rows = events.map((ev) => [
      categoryMap[ev.category]?.label || ev.category,
      formatTurkishDate(ev.event_datetime).replace("·", "-"),
      ev.whose_event, ev.families, ev.venue, ev.notifier_name, ev.contact_number,
      ev.status === "approved" ? "Onayli" : ev.status === "pending" ? "Bekliyor" : "Red",
    ]);
    autoTable(doc, {
      startY: 100,
      head: [["Kategori", "Tarih", "Kimin", "Aileler", "Mekan", "Bildiren", "Iletisim", "Durum"]],
      body: rows,
      styles: { font: "helvetica", fontSize: 9, cellPadding: 6 },
      headStyles: { fillColor: [138, 37, 44], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [249, 246, 240] },
      columnStyles: { 3: { cellWidth: 100 }, 4: { cellWidth: 100 } },
    });
    doc.save(`sahder-etkinlik-raporu-${now.toISOString().slice(0, 10)}.pdf`);
    toast.success("PDF raporu indirildi");
  };

  const renderActions = (ev) => (
    <div className="flex gap-1.5 justify-end flex-wrap">
      {ev.status !== "approved" && (
        <Button size="sm" onClick={() => handleApprove(ev)} data-testid={`approve-btn-${ev.id}`}
          className="bg-[#4A5D23] hover:bg-[#3d4c1c] text-white h-8 gap-1">
          <Check className="w-3.5 h-3.5" /> Onayla
        </Button>
      )}
      {ev.status !== "rejected" && (
        <Button size="sm" variant="outline" onClick={() => handleReject(ev)} data-testid={`reject-btn-${ev.id}`}
          className="border-stone-300 text-[#7F1D1D] hover:bg-[#7F1D1D]/5 h-8 gap-1">
          <X className="w-3.5 h-3.5" /> Reddet
        </Button>
      )}
      {ev.invitation_url && (
        <button
          type="button"
          onClick={() => openInvitation(ev.invitation_url)}
          data-testid={`invitation-btn-${ev.id}`}
          className="inline-flex items-center gap-1 h-8 px-3 rounded-md border border-[#D4AF37]/60 text-[#8A252C] hover:bg-[#D4AF37]/10 text-sm font-medium"
        >
          <ImageIcon className="w-3.5 h-3.5" /> Davetiyeyi Gör
        </button>
      )}
      <Button size="sm" variant="outline" onClick={() => setEditEvent(ev)} data-testid={`edit-btn-${ev.id}`}
        className="border-stone-300 h-8 gap-1">
        <Pencil className="w-3.5 h-3.5" /> Düzenle
      </Button>
      <Button size="sm" variant="outline" onClick={() => setShareEvent(ev)} data-testid={`share-btn-${ev.id}`}
        className="border-[#D4AF37]/60 text-[#8A252C] hover:bg-[#D4AF37]/10 h-8 gap-1">
        <Share2 className="w-3.5 h-3.5" /> Paylaş
      </Button>
      <Button size="sm" variant="ghost" onClick={() => handleDelete(ev)} data-testid={`delete-btn-${ev.id}`}
        className="text-[#7F1D1D] hover:bg-[#7F1D1D]/5 h-8 w-8 p-0" title="Sil">
        <Trash2 className="w-3.5 h-3.5" />
      </Button>
    </div>
  );

  return (
    <div className="space-y-8" data-testid="admin-panel">
      {/* Header */}
      <section className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-[#8A252C] text-white flex items-center justify-center shadow-md ring-4 ring-[#D4AF37]/30">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A252C]/80 font-bold">Yönetim</div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#1F2937]">Yönetici Paneli</h1>
            <p className="text-sm text-[#4B5563] mt-1">Etkinlikleri onaylayın, paylaşın, ayarları düzenleyin.</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={fetchEvents} data-testid="refresh-btn" className="border-stone-300 gap-2">
            <RefreshCw className="w-4 h-4" /> Yenile
          </Button>
          <Button onClick={() => setManualOpen(true)} data-testid="manual-add-btn"
            className="bg-[#4A5D23] hover:bg-[#3e4e1d] text-white gap-2">
            <Plus className="w-4 h-4" /> Yeni Etkinlik
          </Button>
          <Button onClick={handleGeneratePDF} data-testid="generate-pdf-btn" className="bg-[#1F2937] hover:bg-[#111827] text-white gap-2">
            <FileText className="w-4 h-4" /> PDF Raporu
          </Button>
          <Button variant="outline" onClick={() => setShareCalendar(true)} data-testid="share-calendar-btn"
            className="border-stone-300 gap-2">
            <CalendarRange className="w-4 h-4" /> Etkinlik Listesini Paylaş
          </Button>
          <Button variant="ghost" onClick={handleLogout} data-testid="admin-logout-btn" className="text-[#4B5563] gap-2">
            <LogOut className="w-4 h-4" /> Çıkış
          </Button>
        </div>
      </section>

      {/* Stat cards */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Toplam", value: events.length, color: "#1F2937", testid: "stat-total" },
          { label: "Bekleyen", value: pending.length, color: "#D97706", testid: "stat-pending" },
          { label: "Onaylı", value: approved.length, color: "#4A5D23", testid: "stat-approved" },
          { label: "Reddedilen", value: rejected.length, color: "#7F1D1D", testid: "stat-rejected" },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl border border-stone-200 shadow-sm p-5" data-testid={s.testid}>
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold" style={{ color: s.color }}>{s.label}</div>
            <div className="font-display text-4xl font-bold text-[#1F2937] mt-2">{s.value}</div>
          </div>
        ))}
      </section>

      {/* Tabs */}
      <section>
        <Tabs defaultValue="pending" className="w-full">
          <TabsList className="bg-white border border-stone-200 p-1 rounded-full h-auto flex-wrap">
            <TabsTrigger value="pending" data-testid="tab-pending" className="rounded-full data-[state=active]:bg-[#8A252C] data-[state=active]:text-white px-4 sm:px-5 py-2">
              Bekleyen ({pending.length})
            </TabsTrigger>
            <TabsTrigger value="approved" data-testid="tab-approved" className="rounded-full data-[state=active]:bg-[#8A252C] data-[state=active]:text-white px-4 sm:px-5 py-2">
              Onaylı ({approved.length})
            </TabsTrigger>
            <TabsTrigger value="rejected" data-testid="tab-rejected" className="rounded-full data-[state=active]:bg-[#8A252C] data-[state=active]:text-white px-4 sm:px-5 py-2">
              Reddedilen ({rejected.length})
            </TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all" className="rounded-full data-[state=active]:bg-[#8A252C] data-[state=active]:text-white px-4 sm:px-5 py-2">
              Tümü ({events.length})
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings" className="rounded-full data-[state=active]:bg-[#8A252C] data-[state=active]:text-white px-4 sm:px-5 py-2 gap-2">
              <SettingsIcon className="w-3.5 h-3.5" />
              Ayarlar
            </TabsTrigger>
            <TabsTrigger value="ads" data-testid="tab-ads" className="rounded-full data-[state=active]:bg-[#8A252C] data-[state=active]:text-white px-4 sm:px-5 py-2 gap-2">
              <Megaphone className="w-3.5 h-3.5" />
              Reklam
            </TabsTrigger>
            <TabsTrigger value="social" data-testid="tab-social" className="rounded-full data-[state=active]:bg-[#8A252C] data-[state=active]:text-white px-4 sm:px-5 py-2 gap-2">
              <i className="fa-brands fa-instagram text-sm" />
              Sosyal / Story
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="mt-5">
            {loading ? <LoadingBlock /> : <EventsTable events={pending} actions={renderActions} emptyText="Bekleyen etkinlik yok." />}
          </TabsContent>
          <TabsContent value="approved" className="mt-5">
            {loading ? <LoadingBlock /> : <EventsTable events={approved} actions={renderActions} emptyText="Onaylı etkinlik yok." />}
          </TabsContent>
          <TabsContent value="rejected" className="mt-5">
            {loading ? <LoadingBlock /> : <EventsTable events={rejected} actions={renderActions} emptyText="Reddedilen etkinlik yok." />}
          </TabsContent>
          <TabsContent value="all" className="mt-5">
            {loading ? <LoadingBlock /> : <EventsTable events={events} actions={renderActions} emptyText="Kayıt yok." />}
          </TabsContent>
          <TabsContent value="settings" className="mt-5">
            <AdminSettings />
          </TabsContent>
          <TabsContent value="ads" className="mt-5">
            <AdManagement />
          </TabsContent>
          <TabsContent value="social" className="mt-5">
            <SocialAndStoriesAdmin />
          </TabsContent>
        </Tabs>
      </section>

      {/* Share Event Dialog */}
      {shareEvent && (
        <ShareDialog
          open={!!shareEvent}
          onOpenChange={(o) => !o && setShareEvent(null)}
          title="Etkinliği Paylaş"
          subtitle="Görseli sosyal medyada, WhatsApp'ta paylaşabilir veya PNG olarak indirebilirsiniz."
          fileNamePrefix={`sahder-${shareEvent.category}`}
          buildText={() => buildEventShareText(shareEvent, categoryMap)}
        >
          {(ref) => <SocialMediaCard ref={ref} event={shareEvent} />}
        </ShareDialog>
      )}

      {/* Share Upcoming Events List (no calendar image, no past events) */}
      {shareCalendar && (
        <ShareDialog
          open={shareCalendar}
          onOpenChange={setShareCalendar}
          title="Etkinlik Listesini Paylaş"
          subtitle="Yaklaşan etkinliklerin listesini görsel olarak paylaşın (geçmiş etkinlikler dahil edilmez)."
          fileNamePrefix="sahder-etkinlik-listesi"
          captureRef={calendarShareRef}
          whatsappImageOnly={true}
          buildText={() => {
            const now = new Date();
            const list = approved
              .filter((ev) => new Date(ev.event_datetime) >= now)
              .sort((a, b) => new Date(a.event_datetime) - new Date(b.event_datetime))
              .slice(0, 12)
              .map((ev) => {
                const cat = categoryMap[ev.category]?.label || ev.category;
                return `• ${formatTurkishDate(ev.event_datetime)} — ${cat}: ${ev.whose_event} @ ${ev.venue}`;
              })
              .join("\n");
            return `ŞAHDER Yaklaşan Etkinlikler\n\n${list || "Şu anda kayıtlı yaklaşan etkinlik yok."}\n\nDetaylar için derneğe başvurun.`;
          }}
        >
          <AutoFitPreview>
            <UpcomingListCard ref={calendarShareRef} events={approved} />
          </AutoFitPreview>
        </ShareDialog>
      )}

      {/* Manual Event Add Dialog */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="max-w-3xl bg-[#F9F6F0] border-stone-200 max-h-[92vh] overflow-y-auto" data-testid="manual-event-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#1F2937]">Manuel Etkinlik Ekle</DialogTitle>
            <p className="text-sm text-[#4B5563]">
              Bu formdan eklenen etkinlik otomatik onaylanır ve doğrudan takvimde görünür.
            </p>
          </DialogHeader>
          <EventForm
            submitLabel="Etkinliği Ekle"
            successText="Etkinlik otomatik onaylandı ve takvime eklendi."
            onSubmit={(payload) => apiAdmin.post("/events/admin", payload).then((r) => r.data)}
            onCreated={() => {
              fetchEvents();
              // Auto-close after success flash
              setTimeout(() => setManualOpen(false), 1200);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Event Dialog */}
      <EditEventDialog
        event={editEvent}
        open={!!editEvent}
        onOpenChange={(o) => !o && setEditEvent(null)}
        onSaved={() => fetchEvents()}
      />
    </div>
  );
};

const LoadingBlock = () => (
  <div className="bg-white rounded-2xl border border-stone-200 p-10 text-center text-[#4B5563]">Yükleniyor...</div>
);

export default AdminPanel;
